import {assert} from 'Asserts';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import StorableSummaryManager from 'models/managers/StorableSummaryManager';
import {
  ColumnDefinition,
  getColumnDefinitionIndex,
  ParentStorableColumnDefinition,
  PositionColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {toSpreadsheetIndex, ValueError} from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  resolveNumberOfContainersTypedValue,
  resolveParentStorableTypedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  FinalArrayTypedValue,
  NumberTypedValue,
  StorablesTypedValue,
  StringTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {ParentStorableType} from 'samples/storables/StorableSchemaConstants';

export function setCustomEvalForPosition(
  columnDefinitions: Array<ColumnDefinition>,
  cellValue: Cell,
  rowIndex: number
): Cell {
  const parentStorableColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.PARENT_STORABLE,
    tableColumnDefinitions: columnDefinitions,
  });
  const numContainersColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.NUMBER_OF_CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
  });
  return {
    ...cellValue,
    customEvalArgs: [
      `=${toSpreadsheetIndex({
        row: rowIndex,
        column: parentStorableColumnIndex,
      })}`,
      `=${toSpreadsheetIndex({
        row: rowIndex,
        column: numContainersColumnIndex,
      })}`,
    ],
    customEval: positionCustomEval,
    customEvalOptions: {parentStorableColumnDefinition: columnDefinitions[parentStorableColumnIndex]},
  };
}

/**
 * Validate positions are valid in parent grid and that the number of positions
 * matches the number of containers.
 *
 * This function checks that everything is the correct type, and then delegates to
 * postValidationPositionCustomEval.
 */
export function positionCustomEval(
  positionTypedValue: TypedValue,
  positionColumnDefinition: PositionColumnDefinition,
  [parentStorableTypedValue, numContainersTypedValue]: [TypedValue, TypedValue],
  {parentStorableColumnDefinition}: {parentStorableColumnDefinition: ParentStorableColumnDefinition},
  resolveFromResolutionQuery: ResolveQueryFn
): {val: TypedValue; error: ValueError | null; locked: false} {
  const formattedValue = formatTypedValue(positionTypedValue);
  const val: StringTypedValue = {
    type: BaseTypedValueType.STRING,
    rawValue: formattedValue,
  };

  if (formattedValue === '') {
    return {val, error: new ValueError('Position is required.'), locked: false};
  }

  // The evaluator currently does not return the error, so we need to resolve the cell ourselves.
  // In the future we can consider making the evaluator return the error, but for now this is a rare
  // enough case that it makes sense to handle individually.
  // The evaluator does return the resolved value.
  const {error: numContainersError} = resolveNumberOfContainersTypedValue(numContainersTypedValue);
  const {error: parentStorableError} = resolveParentStorableTypedValue(
    resolveFromResolutionQuery,
    parentStorableTypedValue,
    {
      isForTransfer: true,
      parentStorableType: ParentStorableType.GRID,
      containerSchemaId: parentStorableColumnDefinition.storableSchema
        ? parentStorableColumnDefinition.storableSchema.id
        : null,
    }
  );

  // Stop evaluation if any of the dependencies have errors.
  if (numContainersError != null || parentStorableError != null) {
    return {val, error: null, locked: false};
  }

  assert(
    parentStorableTypedValue.type === BaseTypedValueType.STORABLES,
    'Expected resolution to throw an error if parent storable cannot be converted to type STORABLES'
  );
  assert(
    numContainersTypedValue.type === BaseTypedValueType.NUMBER,
    'Expected resolution to throw an error if number of containers cannot be converted to type NUMBER'
  );
  return postValidationPositionCustomEval(
    val,
    [parentStorableTypedValue, numContainersTypedValue],
    resolveFromResolutionQuery
  );
}

export function postValidationPositionCustomEval(
  val: StringTypedValue,
  [parentStorableTypedValue, numContainersTypedValue]: [StorablesTypedValue, NumberTypedValue],
  resolveFromResolutionQuery: ResolveQueryFn
): {val: TypedValue; error: ValueError | null; locked: false} {
  const positionResolvedValue = resolveFromResolutionQuery(
    val,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.LEGACY_TRANSFER_TABLE_VALIDATE_GRID_COORDINATES,
      positionTypedValue: val,
      parentStorableTypedValue,
    },
    makeTypedValueFromCustomResolvedValue
  );
  if (positionResolvedValue.error) {
    return {
      val,
      error: new ValueError(positionResolvedValue.error.message),
      locked: false,
    };
  }

  const parsedPositions = positionResolvedValue.resolvedValue as FinalArrayTypedValue;

  const numContainers = numContainersTypedValue.rawValue;
  if (parsedPositions.rawValue.length !== numContainers) {
    return {
      val,
      error: new ValueError('Number of positions must match number of containers.'),
      locked: false,
    };
  }

  return {val, error: null, locked: false};
}
